/**
 * The Content-Security-Policy enforced on every previewed HTML document.
 *
 * Deny-by-default (`default-src 'none'`) with two narrow re-grants so an
 * agent-authored page can still lay itself out and run its own inline script:
 * inline `<style>` and inline `<script>` are allowed, `data:` images are
 * allowed, and nothing else is. `connect-src 'none'` is what actually stops
 * `fetch`/`XMLHttpRequest`/`WebSocket`/`EventSource`/`sendBeacon`;
 * `form-action 'none'` blocks submission a second time, independently of the
 * frame's absent `allow-forms`; `base-uri 'none'` stops the document rewriting
 * its own base URL.
 *
 * `sandbox allow-scripts` is in the policy for the *top-level* load. The
 * frame's `sandbox` attribute only constrains the document while it is in that
 * frame: "open frame in new tab", or copying the `blob:` URL out of `src` and
 * pasting it, loads the same document with no frame and therefore no `sandbox`
 * attribute — and a `blob:` URL inherits the creating document's origin, so
 * the page's inline script would then run same-origin with the app
 * (localStorage, cookies, session). `allow-scripts` matches the frame exactly:
 * scripts are meant to run, nothing else is granted, and in particular not
 * `allow-same-origin`, which would hand back the origin this directive exists
 * to take away. The frame's own `sandbox="allow-scripts"` attribute is
 * unchanged and remains the primary boundary for the in-frame case.
 *
 * **It does not, on its own, close that hole, and must not be read as if it
 * did.** `sandbox` is one of the directives the HTML standard requires a
 * `<meta http-equiv>` policy to *ignore* — it is honoured only in a real
 * `Content-Security-Policy` response header or, where supported, the frame's
 * `csp` attribute. The injected `<meta>` therefore carries the directive
 * without enforcing it, and the `csp` attribute is absent in precisely the
 * top-level case above. Closing the hole properly needs the bytes to arrive
 * with a response header — a served preview endpoint rather than a `blob:`
 * URL — which is a backend change outside this spec. The directive is kept
 * because it costs nothing, is correct the moment a header exists, and the
 * `csp` attribute honours it today where it is supported; the residual gap is
 * recorded in the feature's evidence and raised as a decision on the task.
 *
 * Exported once and used in *both* places — the frame's `csp` attribute and
 * the injected `<meta http-equiv>` — so the two can never drift.
 *
 * This module exists rather than a block inside `HtmlPreview.tsx` for
 * Architecture Rule 2.3 (components render, hooks behave: a pure string
 * transform belongs outside the render path, named and unit-tested) and
 * because a component file that also exports constants loses React fast
 * refresh — the same reason `viewIds.ts` is its own file.
 */
export const HTML_PREVIEW_CSP =
  "default-src 'none'; style-src 'unsafe-inline'; script-src 'unsafe-inline'; img-src data:; connect-src 'none'; form-action 'none'; base-uri 'none'; sandbox allow-scripts";

/**
 * How many characters of `html` are the document *prologue*: leading
 * whitespace, an optional XML declaration, and an optional doctype, in that
 * order. The policy is injected at exactly this offset.
 *
 * Deliberately a hand-written scan from offset 0 rather than a pattern match
 * (F5): the intent is an offset no authored markup can move. The scanner
 * consumes the two constructs the standard allows in front of a document and
 * stops at the first character that is neither.
 *
 * **That intent is not currently achieved, and this comment must not be read as
 * if it were (F21, open).** The quote-skipping inside `consume` below calls
 * `html.indexOf(char, scan + 1)`, which searches the **whole document**, not
 * just the construct being consumed. A page that opens a quote in its doctype
 * and never closes it inside the doctype, but writes an even number of further
 * quotes later on, can have those quotes paired across its own body — moving
 * the injection point arbitrarily far into the page, so the policy lands
 * *after* content the page authored, including a `<script>`. F21 is a separate
 * blocker routed to a person for a decision between two bounded options; it is
 * deliberately **not** fixed here, and the behaviour described above is what
 * the code does today.
 *
 * The prologue has to be skipped rather than injected ahead of, because a
 * doctype that is no longer first stops being a doctype and the document drops
 * into quirks mode.
 */
/** The whitespace HTML allows in front of a document. Listed, not matched. */
const PROLOGUE_WHITESPACE = new Set([' ', '\t', '\n', '\r', '\f']);

function prologueEnd(html: string): number {
  let at = 0;

  const skipWhitespace = () => {
    while (at < html.length && PROLOGUE_WHITESPACE.has(html[at])) at += 1;
  };

  /**
   * Consume `open … close` when it starts right here. Returns whether it did.
   *
   * The close delimiter is found by scanning character by character from the
   * end of `open`, skipping `"`-quoted and `'`-quoted sections, because both
   * constructs allow quoted strings that may themselves contain the close
   * delimiter: `<!DOCTYPE html SYSTEM "a>b">` ends at the *second* `>`, not
   * the one inside the quotes. A plain `indexOf` stopped at the quoted `>`,
   * which put the injection offset *inside* the doctype token — the `<meta>`
   * was then swallowed as doctype text and the document rendered with no
   * in-document policy at all.
   *
   * An unterminated construct — an unclosed quote, or no close delimiter — is
   * *not* consumed: the prologue then ends at its `<`, which puts the policy
   * ahead of the malformed text rather than inside it.
   *
   * The quote skip below *does* search past this construct: `indexOf` runs to
   * the end of the document, so a quote opened here can be closed by a quote
   * the page wrote in its body, and every character in between then becomes
   * part of the "prologue" — which is the F21 gap described on `prologueEnd`.
   * Left as-is on purpose; F21 is a separate, pending decision.
   */
  const consume = (open: string, close: string): boolean => {
    if (
      !html
        .slice(at, at + open.length)
        .toLowerCase()
        .startsWith(open)
    ) {
      return false;
    }
    let scan = at + open.length;
    while (scan < html.length) {
      const char = html[scan];
      if (char === '"' || char === "'") {
        const closeQuote = html.indexOf(char, scan + 1);
        // Unterminated quote: the construct has no end, so consume nothing.
        if (closeQuote === -1) return false;
        scan = closeQuote + 1;
        continue;
      }
      if (html.startsWith(close, scan)) {
        at = scan + close.length;
        return true;
      }
      scan += 1;
    }
    return false;
  };

  skipWhitespace();
  if (consume('<?xml', '?>')) skipWhitespace();
  if (consume('<!doctype', '>')) skipWhitespace();
  return at;
}

function metaTag(policy: string): string {
  return `<meta http-equiv="Content-Security-Policy" content="${policy}">`;
}

/**
 * Return `html` with `policy` injected as a `<meta http-equiv>` so enforcement
 * travels with the document itself and does not depend on the frame's `csp`
 * attribute, whose browser support is incomplete.
 *
 * Pure: no DOM, no parser, no dependency. The `<meta>` goes at the document
 * prologue — after any XML declaration and doctype, before every other
 * character — *unconditionally*, whatever the markup looks like. A browser
 * hoists a leading `<meta>` into the head it synthesises, so the policy is
 * still the head's first child in the parsed document, and it precedes any
 * `<script>` or `<link>` the page carries.
 *
 * Injecting at a fixed offset rather than at a located `<head>` is the point
 * (F5). The previous version found the insertion point with a regex over
 * untrusted markup, so the first *textual* `<head …>` won even when it sat
 * inside a comment, a `<script>` or `<textarea>` body, a text node or an
 * attribute value: the `<meta>` was spliced into inert text, corrupting that
 * text and leaving the document with no policy at all. The prologue is the one
 * position a page cannot author its way out of, which is what makes
 * `htmlPreviewCsp.test.ts`'s "present exactly once" invariant a bypass check
 * rather than a formatting check. The `sandbox="allow-scripts"` opaque origin
 * remains the primary boundary; this is the defence-in-depth layer, and it must
 * not be bypassable.
 *
 * A page that already carries its own `<meta http-equiv>` CSP keeps it, byte
 * for byte: the viewer's policy is injected *ahead of* the page's and the
 * page's is never removed, replaced or edited. This is deliberate and is the
 * line most likely to be "fixed" wrongly later. Multiple policies on one
 * document **intersect** rather than override — each is enforced in full, so
 * the page's own policy can only make the result *stricter* than the viewer's,
 * never weaker. Stripping it would therefore relax a document the page author
 * chose to constrain, and would also mean parsing untrusted markup to find it.
 */
export function withInjectedCsp(html: string, policy: string): string {
  const at = prologueEnd(html);
  return html.slice(0, at) + metaTag(policy) + html.slice(at);
}
